import { AuthModalConfig } from '@porterin/booking-flow/src/auth/domain/types/AuthModalConfig';
import AuthModal from '@porterin/booking-flow/src/auth/view/components';
import { AuthContextType as BookingFlowAuthContextType } from '@porterin/booking-flow/src/order-review/view/contexts/auth/AuthContext';
import { authContextConverter } from '@porterin/feature/src/containers/bindings/helpers/BookingFlowBindingsHelper';
import BookingDetailsModal from '@porterin/feature/src/containers/fare-estimate-v1/view/components/order-address-card/view/BookingDetailsModal';
import { AuthContextType } from '@porterin/feature/src/containers/login/view-model/contexts/auth-context/AuthContextV1';
import AttachVehicleModal from '@porterin/feature/src/containers/partners/view/components/AttachVehicleModal';
import PaymentStatusModalCmp, {
  PaymentStatus,
} from '@porterin/feature/src/containers/pnm/view/components/PaymentStatusModalCmp';
import PnmBookingFlowModalCmp from '@porterin/feature/src/containers/pnm/view/components/PnmBookingFlowModalCmp';
import PnMCallbackConfirmationModalCmp from '@porterin/feature/src/containers/pnm/view/components/PnMCallbackConfirmationModalCmp';
import PnMFareEstimateModalCmp from '@porterin/feature/src/containers/pnm/view/components/PnMFareEstimateModalCmp';
import {
  CityService,
  CityServices,
  FareEstimateContent,
  ImageEntity,
  TextSubtext,
} from '@porterin/feature/src/shared/cities/domain/entities/CitiesEntity';
import CitySelectorModalCmp from '@porterin/feature/src/shared/city-selector/view/components/CitySelectorModalCmp';
import FareEstimateModalCmp from '@porterin/feature/src/shared/modal/view/components/FareEstimateModalCmp';
import VehicleFareEstimateModalCmp from '@porterin/feature/src/shared/modal/view/components/VehicleFareEstimateModalCmp';
import SpotRequirementModal from '@porterin/feature/src/shared/spot-lead-flow/view/components/SpotRequirementModal';
import VehicleCitySelectorModalCmp, {
  VehicleSelectorCity,
} from '@porterin/feature/src/shared/vehicles/view/components/VehicleCitySelectorModalCmp';
import ErrorModal from '@porterin/feature/src/ui/components/ErrorModal';
import React, { Dispatch, ReactElement, SetStateAction, useState } from 'react';

import type { ModalVariant } from './ModalContext';

export type ModalDispatch = Dispatch<SetStateAction<ModalVariant | undefined>>;

const MODAL_TAG = {
  ERROR_MODAL: 0,
  VEHICLE_FARE_ESTIMATE: 1,
  VEHICLE_CITY_SELECTOR: 2,
  EDIT_ORDER_REVIEW_ADDRESS: 4,
  BOOKING_DETAILS: 5,
  SPOT_REQUIREMENT: 6,
  CITY_SELECTOR: 7,
  FARE_ESTIMATE_MODAL: 9,
  ATTACH_VEHICLE_MODAL: 10,
  AUTH: 11,
  PNM_BOOKING_FLOW: 14,
  PNM_CALLBACK_CONFIRMATION_MODAL: 15,
  PNM_FARE_ESTIMATE_MODAL: 16,
  PAYMENT_STATUS_MODAL: 17,
} as const;

const WAYPOINT = { PICKUP: 0, DROPOFF: 1 } as const;

const ERROR_SOURCE_FARE_ESTIMATE = 0;
const ERROR_SOURCE_ORDER_REVIEW_MIN = 3;

const AUTH_STATE = {
  SIGNUP_SCREEN: 0,
  SIGNUP_REQ_SELECTION: 1,
  OTP_VALIDATION: 2,
  PROFILE: 3,
  LOGIN_SCREEN_V2: 4,
  SIGNUP_SCREEN_V2: 5,
  OTP_VALIDATION_V2: 6,
} as const;

function authVariant(authState: unknown): 'login' | 'signup' | 'otp' {
  if (typeof authState !== 'number') {
    return 'login';
  }
  switch (authState) {
    case AUTH_STATE.LOGIN_SCREEN_V2:
      return 'login';
    case AUTH_STATE.OTP_VALIDATION:
    case AUTH_STATE.OTP_VALIDATION_V2:
      return 'otp';
    default:
      return 'signup';
  }
}

export function getModalComponent(
  modalState: ModalVariant | undefined,
  toggleModal: ModalDispatch,
): ReactElement | null {
  if (modalState === undefined) {
    return null;
  }

  const close = (): void => toggleModal(undefined);

  switch (modalState.TAG) {
    case MODAL_TAG.ERROR_MODAL: {
      const errorSource = modalState._2 as number;
      const toggleBooking = (waypoint: number): void => {
        if (errorSource === ERROR_SOURCE_FARE_ESTIMATE) {
          toggleModal({
            TAG: MODAL_TAG.BOOKING_DETAILS,
            _0: true,
            _1: waypoint,
            _2: true,
          });
        } else if (errorSource >= ERROR_SOURCE_ORDER_REVIEW_MIN) {
          toggleModal({
            TAG: MODAL_TAG.EDIT_ORDER_REVIEW_ADDRESS,
            _0: true,
            _1: waypoint,
          });
        }
      };
      return (
        <ErrorModal
          modal_state={modalState._0 as boolean}
          close_modal={close}
          error={modalState._1 as number}
          error_source={errorSource}
          toggle_booking_pickup={(): void => toggleBooking(WAYPOINT.PICKUP)}
          toggle_booking_dropoff={(): void => toggleBooking(WAYPOINT.DROPOFF)}
        />
      );
    }

    case MODAL_TAG.VEHICLE_FARE_ESTIMATE:
      return (
        <VehicleFareEstimateModalCmp
          modal={{ modal_state: modalState._0 as boolean, close_modal: close }}
          service_category={modalState._1 as string}
          vehicle={
            modalState._2 as React.ComponentProps<
              typeof VehicleFareEstimateModalCmp
            >['vehicle']
          }
          fare_estimate_form_content={
            modalState._3 as React.ComponentProps<
              typeof VehicleFareEstimateModalCmp
            >['fare_estimate_form_content']
          }
        />
      );

    case MODAL_TAG.VEHICLE_CITY_SELECTOR:
      return (
        <VehicleCitySelectorModalCmp
          modal={{ modal_state: modalState._0 as boolean, close_modal: close }}
          city_selector_data={modalState._1 as VehicleSelectorCity[]}
          service_category={modalState._2}
          city_title_desc={modalState._4 as TextSubtext}
          close_icon={modalState._5 as ImageEntity}
        />
      );

    case MODAL_TAG.BOOKING_DETAILS: {
      const isOpen = modalState._0 as boolean;
      const waypoint = modalState._1 as number;
      const isAddressModalOpen = modalState._2 as boolean;
      return (
        <BookingDetailsModal
          modal_state={isOpen}
          close_modal={(): void =>
            toggleModal({
              TAG: MODAL_TAG.BOOKING_DETAILS,
              _0: !isOpen,
              _1: waypoint,
              _2: isAddressModalOpen,
            })
          }
          waypoint={waypoint}
          is_address_modal_open={isAddressModalOpen}
        />
      );
    }

    case MODAL_TAG.SPOT_REQUIREMENT:
      return (
        <SpotRequirementModal
          modal_state={modalState._0 as boolean}
          close_modal={close}
          requirement={modalState._2}
          form={modalState._1}
          requirement_content={modalState._3}
        />
      );

    case MODAL_TAG.CITY_SELECTOR:
      return (
        <CitySelectorModalCmp
          modalState={modalState._0 as boolean}
          close_modal={close}
          city_selector_data={modalState._1 as CityServices[]}
          close_icon={modalState._2 as ImageEntity}
          city_title_desc={modalState._3 as TextSubtext}
        />
      );

    case MODAL_TAG.FARE_ESTIMATE_MODAL:
      return (
        <FareEstimateModalCmp
          modalState={modalState._0 as boolean}
          close_modal={close}
          category_cards_data={modalState._1 as CityService[]}
          fare_estimate_form_content={
            modalState._2 as React.ComponentProps<
              typeof FareEstimateModalCmp
            >['fare_estimate_form_content']
          }
          modal_content={modalState._3 as FareEstimateContent}
        />
      );

    case MODAL_TAG.ATTACH_VEHICLE_MODAL: {
      const isOpen = modalState._0 as boolean;
      return (
        <AttachVehicleModal
          modalState={isOpen}
          close_modal={(): void =>
            toggleModal({ TAG: MODAL_TAG.ATTACH_VEHICLE_MODAL, _0: !isOpen })
          }
        />
      );
    }

    case MODAL_TAG.AUTH: {
      const isOpen = modalState._0 as boolean;
      const authState = modalState._1;
      const prefillData = modalState._2 as
        | { mobile?: string; name?: string; requirement_type?: string }
        | undefined;
      const authContext = modalState._3 as AuthContextType;
      const onOtpValid = modalState._4 as () => void;
      return (
        <AuthModal
          isOpen={isOpen}
          variant={authVariant(authState)}
          config={undefined as unknown as AuthModalConfig}
          onClose={(): void =>
            toggleModal({
              TAG: MODAL_TAG.AUTH,
              _0: !isOpen,
              _1: authState,
              _2: prefillData,
              _3: authContext,
              _4: onOtpValid,
            })
          }
          prefillData={
            prefillData === undefined
              ? { mobile: undefined, name: undefined, requirement: undefined }
              : {
                  mobile: prefillData.mobile,
                  name: prefillData.name,
                  requirement: prefillData.requirement_type,
                }
          }
          authContext={
            authContextConverter(authContext) as BookingFlowAuthContextType
          }
          onOtpValid={onOtpValid}
        />
      );
    }

    case MODAL_TAG.PNM_BOOKING_FLOW: {
      const isOpen = modalState._0 as boolean;
      const srExternalId = modalState._1 as string | undefined;
      const mobile = modalState._2 as string;
      return (
        <PnmBookingFlowModalCmp
          modal_state={isOpen}
          close_modal={(): void =>
            toggleModal({
              TAG: MODAL_TAG.PNM_BOOKING_FLOW,
              _0: !isOpen,
              _1: srExternalId,
              _2: mobile,
            })
          }
          sr_external_id={srExternalId}
          mobile={mobile}
        />
      );
    }

    case MODAL_TAG.PNM_CALLBACK_CONFIRMATION_MODAL: {
      const isOpen = modalState._0 as boolean;
      return (
        <PnMCallbackConfirmationModalCmp
          modal_state={isOpen}
          close_modal={(): void =>
            toggleModal({
              TAG: MODAL_TAG.PNM_CALLBACK_CONFIRMATION_MODAL,
              _0: !isOpen,
            })
          }
        />
      );
    }

    case MODAL_TAG.PNM_FARE_ESTIMATE_MODAL: {
      const isOpen = modalState._0 as boolean;
      const showHouseField = modalState._1 as boolean;
      return (
        <PnMFareEstimateModalCmp
          modal_state={isOpen}
          close_modal={(): void =>
            toggleModal({
              TAG: MODAL_TAG.PNM_FARE_ESTIMATE_MODAL,
              _0: !isOpen,
              _1: showHouseField,
            })
          }
          show_house_field={showHouseField}
        />
      );
    }

    case MODAL_TAG.PAYMENT_STATUS_MODAL: {
      const isOpen = modalState._0 as boolean;
      const paymentStatus = modalState._1 as PaymentStatus;
      const bookingFlow = modalState._2 as number;
      return (
        <PaymentStatusModalCmp
          modal_state={isOpen}
          close_modal={(): void =>
            toggleModal({
              TAG: MODAL_TAG.PAYMENT_STATUS_MODAL,
              _0: !isOpen,
              _1: paymentStatus,
              _2: bookingFlow,
            })
          }
          payment_status={paymentStatus}
          booking_flow={bookingFlow}
        />
      );
    }

    default:
      return null;
  }
}

export function useModal(
  initialState?: ModalVariant,
): [ReactElement | null, ModalVariant | undefined, ModalDispatch] {
  const [modalState, setModalState] = useState<ModalVariant | undefined>(
    initialState,
  );
  const modalComponent = getModalComponent(modalState, setModalState);
  return [modalComponent, modalState, setModalState];
}
